import { computed, ref } from 'vue'
import api from '@/services/api'
import { useNow } from '@/composables/game/time'
import { useLocalStorage } from '@vueuse/core'
import { buildDropeeStorageKey } from '@/config/config'
import { type GiveawayPrize, GiveawayPrizeType, type GiveawayWinner } from '@/types/giveaway'
import { useConfig } from '@/composables/game/config'
import { toOrdinal } from '@/helpers/utils'

const playerTickets = useLocalStorage<number>(buildDropeeStorageKey('playerGiveawayTickets'), 0)
const formSubmitted = useLocalStorage<boolean>(buildDropeeStorageKey('giveawayFormSubmitted'), false)

const winners = ref<Array<GiveawayWinner> | undefined>(undefined)
const drawn = ref(false)

const userPosition = ref<number | null>(null)
const isWinner = computed(() => userPosition.value !== null)

const giveawayPreviousEndTimestamp = ref(0)
const totalTickets = ref(0)
const previousPlayerTickets = ref(0)
const previousTotalTickets = ref(0)

export const useGiveaway = () => {
  const { now } = useNow()
  const { config } = useConfig()

  const giveawayStartTimestamp = computed(() => config.value.game.giveawayStartDate)
  const giveawayEndTimestamp = computed(() => config.value.game.giveawayEndDate)
  const giveawayDrawTimestamp = computed(() => giveawayEndTimestamp.value + (24 * 3600))
  const giveawayMode = computed(() => config.value.game.giveawayMode)

  const countdown = computed(() => {
    const startTimeDiff = giveawayStartTimestamp.value - now.value
    if (startTimeDiff > 0) {
      return 0
    }
    const timeDiff = giveawayEndTimestamp.value - now.value
    if (timeDiff < 0) {
      return 0
    }
    return timeDiff
  })

  const drawCountdown = computed(() => {
    const timeDiff = giveawayDrawTimestamp.value - now.value
    if (timeDiff < 0) {
      return 0
    }
    return timeDiff
  })

  const hasWinners = computed(() => !!winners.value?.length)

  type PrizeEntry = [[number, number], number]
  const tonPrizePool: PrizeEntry[] = [
    [[1, 1], 1000],
    [[2, 2], 500],
    [[3, 3], 300],
    [[4, 10], 50],
    [[11, 25], 30],
    [[26, 50], 16],
  ]

  const tonPrizeList: GiveawayPrize[] = tonPrizePool.map(
    ([[from, to], amount]) => ({
      amount,
      type: GiveawayPrizeType.TON,
      label: from === to
        ? `${toOrdinal(from)} Place`
        : `${from} - ${to}`,
    }),
  )

  const tonPrizeListForWinners: GiveawayPrize[] = tonPrizePool.flatMap(
    ([[from, to], amount]) =>
      Array.from({ length: to - from + 1 }, (_, i) => ({
        amount,
        type: GiveawayPrizeType.TON,
      })),
  )

  const nftPrizeList: GiveawayPrize[] = [
    {
      type: GiveawayPrizeType.NFT,
      label: '1st Place',
      nftMetadata: {
        name: 'Scared Cat #18194',
        lottie: '/lottie-images/nfts/scared-cat-18194.json',
      },
    },
    {
      type: GiveawayPrizeType.NFT,
      label: '2nd Place',
      nftMetadata: {
        name: 'Swiss Watch #5022',
        lottie: '/lottie-images/nfts/swiss-watch-5022.json',
      },
    },
    {
      type: GiveawayPrizeType.NFT,
      label: '3rd Place',
      nftMetadata: {
        name: 'Lol Pop #4651',
        lottie: '/lottie-images/nfts/lol-pop-4651.json',
      },
    },
  ]

  const prizeList = computed(() =>
    giveawayMode.value === 'ton' ? tonPrizeList : nftPrizeList,
  )

  const prizeListForWinners = computed(() =>
    giveawayMode.value === 'ton' ? tonPrizeListForWinners : nftPrizeList,
  )

  const totalPrize = computed(() =>
    giveawayMode.value === 'ton'
      ? tonPrizePool.reduce(
        (sum, [[from, to], amount]) => sum + (to - from + 1) * amount,
        0,
      )
      : undefined,
  )

  async function syncGiveaway() {
    const [currentTickets, previousTickets, winnersState] = await Promise.all([api.getGiveawayTickets(), api.getPreviousGiveawayTickets(), api.getRaffleWinners()])
    playerTickets.value = currentTickets.player
    totalTickets.value = currentTickets.total
    previousPlayerTickets.value = previousTickets.player
    previousTotalTickets.value = previousTickets.total
    giveawayPreviousEndTimestamp.value = previousTickets.endDate
    userPosition.value = winnersState.position
    winners.value = winnersState.winners
    drawn.value = winnersState.currentGiveawayWinnersDrawn

  }

  return {
    countdown,
    drawCountdown,
    playerTickets,
    totalTickets,
    giveawayEndTimestamp,
    giveawayDrawTimestamp,
    giveawayMode,
    hasWinners,
    isWinner,
    syncGiveaway,
    userPosition,
    winners,
    formSubmitted,
    giveawayPreviousEndTimestamp,
    previousPlayerTickets,
    previousTotalTickets,
    drawn,
    totalPrize: totalPrize.value,
    prizeList: prizeList.value,
    prizeListForWinners: prizeListForWinners.value,
  }
}
